import { useEffect, useState } from 'react';
import { RICERCA_ARTICOLI, useArticoli } from '../api/articoli.js';
import { ErroreApi } from '../api/cliente.js';
import {
  type ArticoloProposta,
  type PropostaListino,
  useArticoliProposta,
  useOpzioniTrattative,
  useSalvaArticoloProposta,
  useSalvaProposta,
  useTogliArticoloProposta,
} from '../api/trattative.js';
import { Modale } from '../componenti/Modale.js';
import { VoceArticolo } from '../componenti/VoceArticolo.js';
import { SKU_ASSENTE, nomeArticolo } from './Articoli.js';
import { giornoItaliano, giornoLocale } from '../giorni.js';
import { t } from '../i18n/lingua.js';

/**
 * Una proposta di listino, con dentro i suoi prezzi.
 *
 * È un listino che non è ancora ufficiale: stessi campi di un listino vero —
 * valuta, resa, validità, articoli con sconti e aliquota — più le condizioni
 * commerciali e una nota riservata che nella proposta al cliente non ci va.
 *
 * Testata e articoli stanno insieme perché è così che si compila: si scrive
 * il titolo e subito dopo si mettono i prezzi. Nel v2 sono la stessa pagina,
 * e su questo il v2 ha ragione.
 */
export function ModaleProposta({
  trattativaId,
  proposta,
  onChiudi,
}: {
  trattativaId: number;
  /** Nulla per una proposta nuova. */
  proposta: PropostaListino | null;
  onChiudi: () => void;
}) {
  const salva = useSalvaProposta(trattativaId);
  const opzioni = useOpzioniTrattative();
  const articoli = useArticoliProposta(trattativaId, proposta?.id ?? null);

  const oggi = giornoLocale();
  const [campi, setCampi] = useState({
    name: proposta?.name ?? '',
    proposalDate: giornoItaliano(proposta?.proposalDate) || oggi,
    startDate: giornoItaliano(proposta?.startDate),
    endDate: giornoItaliano(proposta?.endDate),
    currency: proposta?.currency ?? 'EUR',
    shippingMethod: proposta?.shippingMethod ?? 'exw',
    commercialConditions: proposta?.commercialConditions ?? '',
    notes: proposta?.notes ?? '',
    privateNote: proposta?.privateNote ?? '',
  });
  const [rigaAperta, setRigaAperta] = useState<ArticoloProposta | null | undefined>(undefined);

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const errore = salva.error instanceof ErroreApi ? salva.error.message : null;
  const pronto = campi.name.trim() !== '' && campi.proposalDate !== '';

  const conferma = () =>
    salva.mutate(
      {
        id: proposta?.id ?? null,
        name: campi.name.trim(),
        proposalDate: campi.proposalDate,
        startDate: campi.startDate || null,
        endDate: campi.endDate || null,
        currency: campi.currency,
        shippingMethod: campi.shippingMethod,
        commercialConditions: campi.commercialConditions.trim() || null,
        notes: campi.notes.trim() || null,
        privateNote: campi.privateNote.trim() || null,
      },
      { onSuccess: onChiudi },
    );

  return (
    <>
      <Modale
        titolo={proposta ? 'Proposta di listino' : 'Nuova proposta di listino'}
        occhiello={proposta?.name}
        larga
        inCorso={salva.isPending}
        onChiudi={onChiudi}
        azioni={
          <>
            <button className="btn" onClick={onChiudi} disabled={salva.isPending}>
              {t('Chiudi')}
            </button>
            <button
              className="btn btn-primary"
              onClick={conferma}
              disabled={!pronto || salva.isPending}
            >
              {salva.isPending ? 'Salvo…' : 'Salva'}
            </button>
          </>
        }
      >
        {errore && (
          <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
            <span className="avviso-segno">▲</span>
            <span>{errore}</span>
          </div>
        )}

        <div className="fields">
          <div className="field span-2">
            <label htmlFor="pr-titolo">
              {t('Titolo della proposta')}<span className="req">*</span>
            </label>
            <input
              id="pr-titolo"
              className="input"
              placeholder={t('Proposta di listino sughi')}
              value={campi.name}
              autoFocus
              onChange={(e) => cambia('name', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="pr-valuta">{t('Valuta')}</label>
            <select
              id="pr-valuta"
              className="select"
              value={campi.currency}
              onChange={(e) => cambia('currency', e.target.value)}
            >
              {['EUR', 'USD', 'CAD', 'GBP'].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="pr-resa">{t('Resa')}</label>
            <select
              id="pr-resa"
              className="select"
              value={campi.shippingMethod}
              onChange={(e) => cambia('shippingMethod', e.target.value)}
            >
              {(opzioni.data?.shippingMethods ?? ['exw']).map((r) => (
                <option key={r} value={r}>
                  {r.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="pr-data">
              {t('Data della proposta')}<span className="req">*</span>
            </label>
            <input
              id="pr-data"
              className="input"
              type="date"
              value={campi.proposalDate}
              onChange={(e) => cambia('proposalDate', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="pr-dal">{t('Valida dal')}</label>
            <input
              id="pr-dal"
              className="input"
              type="date"
              value={campi.startDate}
              onChange={(e) => cambia('startDate', e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="pr-al">{t('Valida al')}</label>
            <input
              id="pr-al"
              className="input"
              type="date"
              value={campi.endDate}
              onChange={(e) => cambia('endDate', e.target.value)}
            />
          </div>

          <div className="field span-2">
            <label htmlFor="pr-condizioni">{t('Condizioni commerciali')}</label>
            <textarea
              id="pr-condizioni"
              className="input"
              rows={2}
              value={campi.commercialConditions}
              onChange={(e) => cambia('commercialConditions', e.target.value)}
            />
          </div>

          <div className="field span-2">
            <label htmlFor="pr-note">{t('Note')}</label>
            <textarea
              id="pr-note"
              className="input"
              rows={2}
              value={campi.notes}
              onChange={(e) => cambia('notes', e.target.value)}
            />
          </div>

          <div className="field span-2">
            <label htmlFor="pr-riservata">
              {t('Nota riservata')}
              <span className="dim" style={{ fontWeight: 400 }}>
                {' '}
                {t('— non esce nella proposta mandata al cliente')}
              </span>
            </label>
            <textarea
              id="pr-riservata"
              className="input"
              rows={2}
              value={campi.privateNote}
              onChange={(e) => cambia('privateNote', e.target.value)}
            />
          </div>
        </div>

        {proposta ? (
          <div style={{ marginTop: 18 }}>
            <div className="row" style={{ alignItems: 'baseline', marginBottom: 8 }}>
              <label className="etichetta-sezione" style={{ margin: 0 }}>
                {t('Prezzi')}
              </label>
              <button
                className="btn btn-primary"
                style={{ marginLeft: 'auto' }}
                onClick={() => setRigaAperta(null)}
              >
                {t('Aggiungi articolo')}
              </button>
            </div>

            {(articoli.data?.articles ?? []).length === 0 ? (
              <p className="dim" style={{ fontSize: 13 }}>
                {t('Nessun articolo. Una proposta senza prezzi non si può mandare in approvazione.')}
              </p>
            ) : (
              <div className="table-wrap">
                <table className="tabella">
                  <thead>
                    <tr>
                      <th>{t('Articolo')}</th>
                      <th className="destra">{t('Prezzo EXW')}</th>
                      <th className="destra">{t('Costo DDP')}</th>
                      <th className="destra">{t('Sconti')}</th>
                      <th>{t('Aliquota')}</th>
                      <th className="destra">{t('Promo')}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {articoli.data!.articles.map((a) => (
                      <tr key={a.id}>
                        <td>
                          {a.description}
                          {a.sku && a.sku !== SKU_ASSENTE && (
                            <span className="dim num" style={{ marginLeft: 8 }}>
                              {a.sku}
                            </span>
                          )}
                        </td>
                        <td className="destra num">{Number(a.priceExw).toLocaleString('it-IT')}</td>
                        <td className="destra num">{Number(a.costDdp).toLocaleString('it-IT')}</td>
                        <td className="destra num">
                          {[a.discountPercentage1, a.discountPercentage2, a.discountPercentage3]
                            .filter((s) => s && Number(s) !== 0)
                            .map((s) => `${Number(s)}%`)
                            .join(' + ') || <span className="dim">—</span>}
                        </td>
                        <td>{a.tax}</td>
                        <td className="destra num">
                          {a.destinations.length > 0 ? (
                            a.destinations.length
                          ) : (
                            <span className="dim">—</span>
                          )}
                        </td>
                        <td className="destra">
                          <button className="btn btn-ghost" onClick={() => setRigaAperta(a)}>
                            {t('Modifica')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <p className="dim" style={{ fontSize: 12, marginTop: 14, marginBottom: 0 }}>
            {t('I prezzi si aggiungono dopo aver salvato la proposta.')}
          </p>
        )}
      </Modale>

      {proposta && rigaAperta !== undefined && (
        <ModaleArticoloProposta
          trattativaId={trattativaId}
          propostaId={proposta.id}
          riga={rigaAperta}
          onChiudi={() => setRigaAperta(undefined)}
        />
      )}
    </>
  );
}

/** Il prezzo di un articolo dentro la proposta. */
function ModaleArticoloProposta({
  trattativaId,
  propostaId,
  riga,
  onChiudi,
}: {
  trattativaId: number;
  propostaId: number;
  riga: ArticoloProposta | null;
  onChiudi: () => void;
}) {
  const salva = useSalvaArticoloProposta(trattativaId, propostaId);
  const togli = useTogliArticoloProposta(trattativaId, propostaId);
  const opzioni = useOpzioniTrattative();

  const [cerca, setCerca] = useState('');
  const [articoloId, setArticoloId] = useState<number | null>(riga?.articleId ?? null);
  const [articoloNome, setArticoloNome] = useState(riga?.description ?? '');
  const [campi, setCampi] = useState({
    priceExw: riga?.priceExw ?? '',
    costDdp: riga?.costDdp ?? '0',
    taxId: riga ? String(riga.taxId) : '',
    crossReference: riga?.crossReference ?? '',
    discountPercentage1: riga?.discountPercentage1 ?? '',
    discountPercentage2: riga?.discountPercentage2 ?? '',
    discountPercentage3: riga?.discountPercentage3 ?? '',
  });

  // l'aliquota predefinita dell'azienda si propone da sola: è quella giusta
  // quasi sempre, e sceglierla ogni volta è un clic per niente
  useEffect(() => {
    if (campi.taxId === '' && opzioni.data) {
      const predefinita = opzioni.data.taxes.find((t) => t.isDefault) ?? opzioni.data.taxes[0];
      if (predefinita) setCampi((c) => ({ ...c, taxId: String(predefinita.id) }));
    }
  }, [opzioni.data, campi.taxId]);

  const trovati = useArticoli({ ...RICERCA_ARTICOLI, q: cerca, size: 20 });
  const elenco = cerca.trim().length >= 2 ? (trovati.data?.rows ?? []) : [];

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const errore = salva.error instanceof ErroreApi ? salva.error.message : null;
  const numero = (v: string) => (v === '' ? null : Number(v));
  const pronto = (riga !== null || articoloId !== null) && campi.priceExw !== '' && campi.taxId !== '';

  const conferma = () =>
    salva.mutate(
      {
        id: riga?.id ?? null,
        ...(riga ? {} : { articleId: articoloId }),
        priceExw: Number(campi.priceExw),
        costDdp: Number(campi.costDdp || 0),
        taxId: Number(campi.taxId),
        crossReference: campi.crossReference.trim() || null,
        discountPercentage1: numero(campi.discountPercentage1),
        discountPercentage2: numero(campi.discountPercentage2),
        discountPercentage3: numero(campi.discountPercentage3),
      },
      { onSuccess: onChiudi },
    );

  return (
    <Modale
      titolo={riga ? t('Modifica il prezzo') : t('Aggiungi un articolo alla proposta')}
      larga
      inCorso={salva.isPending || togli.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          {riga && (
            <button
              className="btn btn-pericolo"
              onClick={() => togli.mutate(riga.id, { onSuccess: onChiudi })}
              disabled={togli.isPending}
            >
              {t('Togli dalla proposta')}
            </button>
          )}
          <button className="btn" onClick={onChiudi} disabled={salva.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!pronto || salva.isPending}
          >
            {salva.isPending ? 'Salvo…' : 'Salva'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="field span-2" style={{ marginBottom: 14 }}>
        <label htmlFor="ap-articolo">
          {t('Articolo')}<span className="req">*</span>
        </label>
        {riga || articoloId !== null ? (
          <div className="scelto">
            <strong>{articoloNome}</strong>
            {!riga && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setArticoloId(null);
                  setArticoloNome('');
                }}
              >
                {t('Cambia')}
              </button>
            )}
          </div>
        ) : (
          <>
            <input
              id="ap-articolo"
              className="input"
              placeholder={t('Cerca per descrizione, SKU o numero BC')}
              value={cerca}
              autoFocus
              onChange={(e) => setCerca(e.target.value)}
            />
            {elenco.length > 0 && (
              <ul className="risultati">
                {elenco.map((a) => (
                  <li key={a.id}>
                    <button
                      className="risultato"
                      onClick={() => {
                        setArticoloId(a.id);
                        setArticoloNome(nomeArticolo(a));
                        setCerca('');
                      }}
                    >
                      <VoceArticolo articolo={a} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="fields">
        <div className="field">
          <label htmlFor="ap-exw">
            {t('Prezzo EXW')}<span className="req">*</span>
          </label>
          <input
            id="ap-exw"
            className="input num"
            type="number"
            step="0.001"
            min="0"
            value={campi.priceExw}
            onChange={(e) => cambia('priceExw', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ap-ddp">{t('Costo DDP')}</label>
          <input
            id="ap-ddp"
            className="input num"
            type="number"
            step="0.001"
            min="0"
            value={campi.costDdp}
            onChange={(e) => cambia('costDdp', e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ap-aliquota">
            {t('Aliquota')}<span className="req">*</span>
          </label>
          <select
            id="ap-aliquota"
            className="select"
            value={campi.taxId}
            onChange={(e) => cambia('taxId', e.target.value)}
          >
            <option value="">{t('Scegli…')}</option>
            {(opzioni.data?.taxes ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field span-2">
          <label htmlFor="ap-rif">{t('Riferimento del cliente')}</label>
          <input
            id="ap-rif"
            className="input"
            value={campi.crossReference}
            onChange={(e) => cambia('crossReference', e.target.value)}
          />
        </div>
        {([1, 2, 3] as const).map((n) => (
          <div className="field" key={n}>
            <label htmlFor={`ap-sc${n}`}>{t('Sconto {n} (%)', { n })}</label>
            <input
              id={`ap-sc${n}`}
              className="input num"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={campi[`discountPercentage${n}` as keyof typeof campi]}
              onChange={(e) =>
                cambia(`discountPercentage${n}` as keyof typeof campi, e.target.value)
              }
            />
          </div>
        ))}
      </div>

      {riga && riga.destinations.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <label className="etichetta-sezione">{t('Prezzi dedicati a una destinazione')}</label>
          <ul className="doclist">
            {riga.destinations.map((d) => (
              <li key={d.id}>
                <span>
                  {d.address ?? 'Destinazione'}
                  {d.city ? ` — ${d.city}` : ''}
                </span>
                <span className="dim num">{Number(d.priceExw).toLocaleString('it-IT')}</span>
              </li>
            ))}
          </ul>
          <p className="dim" style={{ fontSize: 12 }}>
            {t('Vengono portati nel listino ufficiale insieme al resto.')}
          </p>
        </div>
      )}
    </Modale>
  );
}
